// src/composables/useFlashcards.ts
import { ref, computed, toRaw } from 'vue'
import { TrainingMode } from './useExercise'
import db from '@/db'
import type { Word, Reading, SRSData, Bushu, Kanji } from '@/db'
import type { Collection, Table } from 'dexie'

export type Flashcard =
  | { type: 'word'; id: string; word: Word; srsData: SRSData }
  | {
      type: 'reading'
      id: string
      kanji: Kanji
      onReadings: Reading[]
      kunReadings: Reading[]
      bushu?: Bushu
      srsData: SRSData
    }

/**
 * A helper function that calculates new SRS data based on a quality rating.
 * Here quality is a number:
 *   0: "Again"
 *   1: "Hard"
 *   2: "Good"
 *   3: "Easy"
 *
 * This is a simplified version of the SM-2 algorithm.
 */
function updateSRSData(oldData: SRSData, quality: number): SRSData {
  let { repetitionCount = 0, interval = 0, easeFactor = 2.5 } = oldData
  const now = new Date()

  if (quality < 2) {
    // If rating is too low, reset repetition and interval.
    repetitionCount = 0
    interval = 1 // in days
  } else {
    repetitionCount = (repetitionCount || 0) + 1
    if (repetitionCount === 1) {
      interval = 1
    } else if (repetitionCount === 2) {
      interval = 6
    } else {
      interval = Math.round((interval || 1) * easeFactor)
    }
    // Adjust easeFactor slightly.
    easeFactor = easeFactor - 0.1 + (quality - 2) * 0.1
    if (easeFactor < 1.3) easeFactor = 1.3
  }
  let nextReviewAt = new Date(now.getTime() + interval * 24 * 60 * 60 * 1000)
  if (quality === 0) {
    nextReviewAt = now
  }

  return {
    ...oldData,
    repetitionCount,
    interval,
    easeFactor,
    lastReviewedAt: now,
    nextReviewAt,
    reviewHistory: [
      ...(oldData.reviewHistory || []).map((item) => toRaw(item)),
      { createdAt: now, rating: quality.toString(), intervalBeforeReview: oldData.interval || 0 },
    ],
  }
}

/**
 * The trainingMode parameter defines what type(s) of flashcards to train:
 * - 'words': only train flashcards created from words.
 * - 'readings': only train flashcards created from readings.
 * - 'both': merge both flashcard types and randomize the order.
 *
 * The kanjiFilter parameter allows filtering to specific kanji when provided.
 */
export function useFlashcards(
  trainingMode: TrainingMode = TrainingMode.MIXED_FLASHCARDS,
  kanjiFilter?: string[],
) {
  const flashcards = ref<Flashcard[]>([])
  const currentIndex = ref(0)
  const isRevealed = ref(false)
  const canRate = ref(false)
  const loading = ref(true)
  const currentTrainingMode = ref(trainingMode)

  async function loadFlashcards() {
    loading.value = true
    const now = new Date()
    // Retrieve list of known kanjis (user decisions).
    const userKanjis = await db.userKanjis.toArray()
    // Assuming you mark them to show with a flag (e.g., shouldShow) or use a similar approach:
    let knownChars = userKanjis.filter((item) => item.shouldShow).map((item) => item.kanji)

    // Apply kanji filter if provided (for custom exercises)
    if (kanjiFilter && kanjiFilter.length > 0) {
      knownChars = knownChars.filter((char) => kanjiFilter.includes(char))
    }

    const wordCards: Flashcard[] = []
    const readingCards: Flashcard[] = []

    // If training words or both:
    if (
      currentTrainingMode.value === TrainingMode.WORDS_FLASHCARDS ||
      currentTrainingMode.value === TrainingMode.MIXED_FLASHCARDS
    ) {
      const allWords = await db.words.toArray()
      // Filter words based on whether kanji filter is applied
      const filteredWords = allWords.filter((item) => {
        if (kanjiFilter && kanjiFilter.length > 0) {
          // Get kanji with shouldShow=false to exclude words containing them
          const excludedKanji = userKanjis
            .filter((uk) => uk.shouldShow === false)
            .map((uk) => uk.kanji)

          // Exclude words that contain any kanji with shouldShow=false
          const hasExcludedKanji = item.kanjiSet.some((ch) => excludedKanji.includes(ch))
          if (hasExcludedKanji) {
            return false
          }

          // Include words that contain at least one of the filtered kanji
          return item.kanjiSet.some((ch) => kanjiFilter.includes(ch))
        } else {
          // Default behavior: all kanji in a word must be known
          return item.kanjiSet.every((ch) => knownChars.includes(ch))
        }
      })

      for (const item of filteredWords) {
        let srs = await db.srsData.get(item.id)

        if (!srs) {
          // Create a default in-memory SRS object, to be persisted upon rating.
          srs = {
            id: item.id,
            type: 'word',
            repetitionCount: 0,
            interval: 0,
            easeFactor: 2.5,
            reviewHistory: [],
            nextReviewAt: now,
          } satisfies SRSData
        }
        if (!srs.nextReviewAt || srs.nextReviewAt <= now) {
          wordCards.push({
            type: 'word',
            id: item.id,
            word: item,
            srsData: srs,
          })
        }
      }
    }

    // If training readings or both:
    if (
      currentTrainingMode.value === TrainingMode.READINGS_FLASHCARDS ||
      currentTrainingMode.value === TrainingMode.MIXED_FLASHCARDS
    ) {
      for (const knownChar of knownChars) {
        const kanji = await db.kanjis.get(knownChar)
        if (!kanji) {
          continue
        }
        const onReadings = await db.readings
          .where('kanji')
          .equals(knownChar)
          .and((item) => item.readingType === 'on')
          .toArray()
        const kunReadings = await db.readings
          .where('kanji')
          .equals(knownChar)
          .and((item) => item.readingType === 'kun')
          .toArray()
        const bushu = await db.bushus.get(kanji.bushu || '')
        if (!onReadings.length && !kunReadings.length) {
          continue
        }

        let srs = await db.srsData.get(knownChar)
        if (!srs) {
          // Create a default in-memory SRS object.
          srs = {
            id: knownChar,
            type: 'reading',
            repetitionCount: 0,
            interval: 0,
            easeFactor: 2.5,
            reviewHistory: [],
            nextReviewAt: now,
          } satisfies SRSData
        }
        if (!srs.nextReviewAt || srs.nextReviewAt <= now) {
          readingCards.push({
            type: 'reading',
            id: knownChar,
            kanji,
            onReadings,
            kunReadings,
            bushu,
            srsData: srs,
          })
        }
      }
    }

    // Combine flashcards based on mode.
    let combinedCards: Flashcard[] = []
    if (currentTrainingMode.value === TrainingMode.MIXED_FLASHCARDS) {
      combinedCards = [...wordCards, ...readingCards]
    } else if (currentTrainingMode.value === TrainingMode.WORDS_FLASHCARDS) {
      combinedCards = wordCards
    } else if (currentTrainingMode.value === TrainingMode.READINGS_FLASHCARDS) {
      combinedCards = readingCards
    }

    // Randomize the order.
    flashcards.value = combinedCards.sort(() => Math.random() - 0.5)

    currentIndex.value = 0
    isRevealed.value = false
    canRate.value = false
    loading.value = false
  }

  // Computed property for the current flashcard.
  const currentFlashcard = computed(() => flashcards.value[currentIndex.value])

  function flipCard() {
    isRevealed.value = !isRevealed.value
    canRate.value = true
  }

  async function rateCurrentFlashcard(quality: number) {
    const card = currentFlashcard.value
    if (!card) return

    const newSRS = updateSRSData(card.srsData, quality)

    // Persist the updated SRS record or add it if not present.
    const existingSRS = await db.srsData.get(card.id)
    if (existingSRS) {
      await db.srsData.put(newSRS)
    } else {
      await db.srsData.add(newSRS)
    }
    card.srsData = newSRS

    if (quality === 0) {
      // offset between 3 and 5
      const offset = Math.floor(Math.random() * 3) + 3
      const insertAt = Math.min(currentIndex.value + offset, flashcards.value.length)
      flashcards.value.splice(insertAt, 0, card)
    }

    // Move to the next flashcard.
    if (currentIndex.value < flashcards.value.length - 1) {
      currentIndex.value += 1
      isRevealed.value = false
      canRate.value = false
    } else {
      // Reload flashcards if at the end.
      await loadFlashcards()
    }
  }

  async function resetFlashcards() {
    let srsQuery: Table | Collection = db.srsData
    switch (currentTrainingMode.value) {
      case TrainingMode.WORDS_FLASHCARDS:
        srsQuery = srsQuery.where('type').equals('word')
        break
      case TrainingMode.READINGS_FLASHCARDS:
        srsQuery = srsQuery.where('type').equals('reading')
        break
    }
    const allSRS = await srsQuery.toArray()
    const date = new Date()
    for (const srs of allSRS) {
      await db.srsData.update(srs.id, { nextReviewAt: date })
    }
    await loadFlashcards()
  }

  return {
    currentFlashcard,
    flashcardsLeft: computed(() => flashcards.value.length - currentIndex.value),
    isRevealed,
    canRate,
    loading,
    flipCard,
    rateCurrentFlashcard,
    loadFlashcards,
    resetFlashcards,
  }
}
